import { type FC, useEffect, useState } from "react";
import type { CompanyICPDto } from "shared";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import "./IcpEditor.css";
import { Placeholder } from "@tiptap/extensions";
import { Markdown } from "@tiptap/markdown";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import cn from "classnames";
import { useEventCallback } from "usehooks-ts";
import { Icon } from "../../common/Icon";
import { MarkdownEditor } from "../../common/markdown/MarkdownEditor";
import { useCompanyIcpActions } from "./useCompanyIcpActions";

type Props = {
	icp: CompanyICPDto;
};
export const IcpEditor: FC<Readonly<Props>> = ({ icp }) => {
	const [editing, setEditing] = useState(false);
	const { handleApprove, handleContentUpdate } = useCompanyIcpActions();
	const editor = useEditor({
		extensions: [
			StarterKit,
			Markdown,
			Placeholder.configure({ placeholder: "Write something..." }),
		],
		editable: editing,
		content: icp.profile || undefined,
		contentType: "markdown",
	});

	useEffect(() => {
		editor.setEditable(editing);
	}, [editing, editor]);

	const handleSave = useEventCallback(async () => {
		setEditing(false);
		await handleContentUpdate(icp, editor.getMarkdown());
	});

	return (
		<Card
			className={`ipc-editor ${cn({ editing: editing, approved: icp.approved, rejected: icp.approved === false })}`}
		>
			<Card.Body>
				{icp.approved === false && (
					<Button onClick={() => handleApprove(icp, null)} variant="link">
						Back
					</Button>
				)}
				{icp.approved !== false && <MarkdownEditor editor={editor} />}
			</Card.Body>
			{icp.approved !== false && (
				<Card.Footer>
					{(icp.approved === null || icp.approved === undefined) &&
						(editing ? (
							<Button onClick={handleSave}>Save Changes</Button>
						) : (
							<>
								<Button onClick={() => handleApprove(icp, true)}>
									Approve
								</Button>
								<Button onClick={() => setEditing(true)}>Edit</Button>
								<Button onClick={() => handleApprove(icp, false)}>
									Remove
								</Button>
							</>
						))}

					{icp.approved && !editing && (
						<>
							<span className="mark-icon">
								<Icon name="verified" size={24} />

								<strong>APPROVED</strong>
							</span>

							<Button onClick={() => handleApprove(icp, null)} variant="link">
								Back
							</Button>
						</>
					)}
				</Card.Footer>
			)}
		</Card>
	);
};
